import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../utils/constant';

const BASE_URL = `${API_URL}/final-products`;

// Fetch all final products with pagination and filters
export const fetchFinalProducts = createAsyncThunk(
  'finalProduct/fetchFinalProducts',
  async ({ companyId, page = 1, limit = 10, filters = {} }, { rejectWithValue, getState }) => {
    try {
      const params = {
        page,
        limit,
        // Product filters
        ...(filters.productName && { productName: filters.productName }),
        ...(filters.categoryId && { categoryId: filters.categoryId }),
        ...(filters.brandId && { brandId: filters.brandId }),
        ...(filters.productValueType && { productValueType: filters.productValueType }),
        // AvailableVariants filters
        ...(filters.potency && { potency: filters.potency }),
        ...(filters.volume && { volume: filters.volume }),
        // Variation filters
        ...(filters.sku && { sku: filters.sku }),
        ...(filters.barcode && { barcode: filters.barcode }),
        ...(filters.serialNo && { serialNo: filters.serialNo }),
        ...(filters.size && { size: filters.size }),
        ...(filters.color && { color: filters.color }),
      };

      // Check if we already have potencies and volumes
      const state = getState();
      const hasPotenciesAndVolumes = state.finalProduct?.potencies?.length > 0 && state.finalProduct?.volumes?.length > 0;
      
      // Add flag to request potencies/volumes only if not already loaded
      if (!hasPotenciesAndVolumes) {
        params.includeFilters = true;
      }

      const response = await axios.get(`${BASE_URL}/${companyId}`, {
        params
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch products');
    }
  }
);

// Add a new final product
export const addFinalProduct = createAsyncThunk(
  'finalProduct/addFinalProduct',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to add product');
    }
  }
);

// Update an existing final product
export const updateFinalProduct = createAsyncThunk(
  'finalProduct/updateFinalProduct',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update product');
    }
  }
);

// Delete a final product
export const deleteFinalProduct = createAsyncThunk(
  'finalProduct/deleteFinalProduct',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE_URL}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete product');
    }
  }
);

// Update product variation
export const updateProductVariation = createAsyncThunk(
  'finalProduct/updateProductVariation',
  async ({ variationId, updateData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/variation/${variationId}`, updateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update variation');
    }
  }
);

// Move quantity to loose
export const moveToLoose = createAsyncThunk(
  'finalProduct/moveToLoose',
  async ({ variationId, loose_qty_to_add, loose_volume_to_add, from_stock_type = 'retail' }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/variation-loose/${variationId}`, {
        loose_qty_to_add,
        loose_volume_to_add,
        from_stock_type
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to move to loose');
    }
  }
);

// Transfer stock between wholesale and retail
export const transferStock = createAsyncThunk(
  'finalProduct/transferStock',
  async ({ variationId, qty_to_transfer, direction }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/transfer-stock/${variationId}`, { qty_to_transfer, direction });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to transfer stock');
    }
  }
);

// Add direct stock to Final Product (wholesale or retail)
export const addDirectStock = createAsyncThunk(
  'finalProduct/addDirectStock',
  async ({ productId, data }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/add-stock/${productId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to add stock');
    }
  }
);

// Fetch transfer logs
export const fetchTransferLogs = createAsyncThunk(
  'finalProduct/fetchTransferLogs',
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/transfer-logs/${companyId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch transfer logs');
    }
  }
);

// Seed packaging/dispensing products
export const seedPackagingProducts = createAsyncThunk(
  'finalProduct/seedPackagingProducts',
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/seed-packaging/${companyId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to seed packaging products');
    }
  }
);

const finalProductSlice = createSlice({
  name: 'finalProduct',
  initialState: {
    products: [],
    potencies: [],
    volumes: [],
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
    loading: false,
    error: null,
    // Track if filters have been loaded
    filtersLoaded: false,
    // Store current filters in state
    filters: {},
    transferLogs: []
  },
  reducers: {
    // Optional: Add a reducer to reset pagination state
    resetPagination: (state) => {
      state.currentPage = 1;
      state.limit = 10;
    },
    // Set filters and reset to page 1
    setFilters: (state, action) => {
      state.filters = action.payload;
      state.currentPage = 1; // Reset to first page when filters change
    },
    // Clear all filters
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    // Manually refresh potencies and volumes if needed
    refreshFilters: (state) => {
      state.filtersLoaded = false;
      state.potencies = [];
      state.volumes = [];
    }
  },

  extraReducers: (builder) => {
    builder
      // Fetch final products
      .addCase(fetchFinalProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFinalProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.data;
        
        // Store pagination metadata from the API response
        if (action.payload.pagination) {
          state.totalCount = action.payload.pagination.totalCount;
          state.totalPages = action.payload.pagination.totalPages;
          state.currentPage = action.payload.pagination.currentPage;
          state.limit = action.payload.pagination.limit;
        }

        // Only update potencies and volumes if they haven't been loaded yet
        // and if the API returned them
        if (!state.filtersLoaded && action.payload.potencies && action.payload.volumes) {
          state.potencies = action.payload.potencies;
          state.volumes = action.payload.volumes;
          state.filtersLoaded = true;
        }
      })
      .addCase(fetchFinalProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add final product
      .addCase(addFinalProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addFinalProduct.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally refresh the list or add to current page
        // state.products.push(action.payload.data);
      })
      .addCase(addFinalProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update final product
      .addCase(updateFinalProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFinalProduct.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.products.findIndex(product => product.id === action.payload.data.id);
        if (index !== -1) {
          state.products[index] = action.payload.data;
        }
      })
      .addCase(updateFinalProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete final product
      .addCase(deleteFinalProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFinalProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.products = state.products.filter(product => product.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(deleteFinalProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update product variation
      .addCase(updateProductVariation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProductVariation.fulfilled, (state, action) => {
        state.loading = false;
        const updatedVariation = action.payload.data;
        state.products.forEach(product => {
          if (product.variations && Array.isArray(product.variations)) {
            const variationIndex = product.variations.findIndex(v => v.id === updatedVariation.id);
            if (variationIndex !== -1) {
              product.variations[variationIndex] = updatedVariation;
            }
          }
        });
      })
      .addCase(updateProductVariation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Move to loose
      .addCase(moveToLoose.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(moveToLoose.fulfilled, (state, action) => {
        state.loading = false;
        const updatedVariation = action.payload.data;
        state.products.forEach(product => {
          if (product.variations && Array.isArray(product.variations)) {
            const variationIndex = product.variations.findIndex(v => v.id === updatedVariation.id);
            if (variationIndex !== -1) {
              product.variations[variationIndex] = updatedVariation;
            }
          }
        });
      })
      .addCase(moveToLoose.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Transfer stock
      .addCase(transferStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transferStock.fulfilled, (state, action) => {
        state.loading = false;
        const updatedVariation = action.payload.data;
        state.products.forEach(product => {
          if (product.variations && Array.isArray(product.variations)) {
            const variationIndex = product.variations.findIndex(v => v.id === updatedVariation.id);
            if (variationIndex !== -1) {
              product.variations[variationIndex] = updatedVariation;
            }
          }
        });
      })
      .addCase(transferStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch transfer logs
      .addCase(fetchTransferLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransferLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.transferLogs = action.payload.data || [];
      })
      .addCase(fetchTransferLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetPagination, setFilters, clearFilters, refreshFilters } = finalProductSlice.actions;
export default finalProductSlice.reducer;